import {
  AssetDataObjType,
  EditableFeatureType,
} from 'features/assetDatabase/types/assetOverviewTypes'
import { CustomField } from 'features/assetDatabase/types/customField'
import { getFormFieldsForFeatureType } from 'features/assetDatabase/utils/assetDBFeatures'
import {
  convertCoordinateString,
  FormField,
  GeckoFeaturesJSON,
  GeckoMeasurement,
  GeckoMeasurementSystem,
  GeckoMeasurementUnit,
  GeckoUnit,
  getFormFieldByDataKey,
  getStringFieldByDataKey,
  SortTableByField,
  UnitMagnitude,
} from 'types'
import { assetModelPrecision } from 'utils/constants'

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

export function generateTableCellsFromFeatureLimitedFields(
  featureTuple: [string, GeckoFeaturesJSON],
  measurementSystem: GeckoMeasurementSystem,
  convertToUnit: (
    measurementSystem: GeckoMeasurementSystem,
    unitMagnitude: UnitMagnitude | GeckoMeasurementUnit | null
  ) => GeckoMeasurementUnit | null,
  fields: string[]
): {
  fieldName: string
  value: string | number | string[] | undefined
}[] {
  const feature = featureTuple[1]
  return fields.map((fieldName) => {
    const formFields = getFormFieldsForFeatureType(feature)
    const formField = getFormFieldByDataKey(formFields, fieldName)
    let value
    if (formField) {
      const displayUnit = convertToUnit(
        measurementSystem,
        formField.unit_magnitude
      )
      const coordinateValue =
        typeof formField.value === 'string' && displayUnit
          ? convertCoordinateString(
              formField.value,
              GeckoMeasurementUnit.Meters,
              displayUnit
            )
          : null
      if (coordinateValue) {
        value = coordinateValue
      } else if (displayUnit && isNumber(formField.value)) {
        value = GeckoMeasurement.CreateFromValue(
          formField.value as number,
          GeckoMeasurementUnit.Meters
        )
          .convertTo(displayUnit)
          .toPrecision(assetModelPrecision).num
        if (
          formField.unit_magnitude === UnitMagnitude.FeetOrMeters ||
          formField.unit_magnitude === UnitMagnitude.InchesOrMillimeters ||
          formField.unit_magnitude === UnitMagnitude.PsiOrMPa ||
          formField.unit_magnitude === UnitMagnitude.FahrenheitOrCelsius
        ) {
          value = value.toFixed(5)
        }
      } else if (Array.isArray(formField.value)) {
        value = formField.value
      } else if (!isNumber(formField.value)) {
        value = formField.value?.replace(/_/g, ' ')
      } else {
        value = formField.value
      }
    }
    return { fieldName, value }
  })
}

export const deleteFeatures = (
  obj: Record<string, unknown>,
  featuresToRemove: string[]
): Record<string, unknown> => {
  return Object.keys(obj).reduce((acc: Record<string, unknown>, key) => {
    if (!featuresToRemove.includes(key)) {
      acc[key] = obj[key]
    }
    return acc
  }, {})
}

export function checkMissingDerivedLayerFields(assetModel: AssetDataObjType) {
  let hasMissingComponentsFields = false,
    hasMissingFeatureFields = false

  const hasMissingDetailsFields = hasMissingFields(
    assetModel.assetFormFields.formFields
  )

  assetModel.components.map((componentModel) => {
    const foundMissingFields = hasMissingFields(
      componentModel.componentData.formFields
    )
    if (foundMissingFields) {
      hasMissingComponentsFields = true
    }
    componentModel.features.map((featureModel) => {
      const foundMissingFields = hasMissingFields(featureModel.formFields)
      if (foundMissingFields) {
        hasMissingFeatureFields = true
      }
    })
  })
  return [
    hasMissingDetailsFields,
    hasMissingComponentsFields,
    hasMissingFeatureFields,
  ]
}

const hasMissingFields = (formFields: FormField[]) => {
  let hasMissingFields = false
  formFields.map((formField) => {
    if (formField.value === undefined && formField.isNeededForDerivedLayer) {
      hasMissingFields = true
    }
  })
  return hasMissingFields
}

export const createUpdatedUnitObject = (
  unit: GeckoUnit,
  convertedFormFields: FormField[],
  customFields: CustomField[]
) => {
  const unitRaw = unit.raw

  const name = getFormFieldByDataKey(convertedFormFields, 'name')
  const erpID = getFormFieldByDataKey(convertedFormFields, 'erp_id')
  const area = getFormFieldByDataKey(convertedFormFields, 'area')
  const areaDescription = getFormFieldByDataKey(
    convertedFormFields,
    'area_description'
  )
  const operationalStatus = getFormFieldByDataKey(
    convertedFormFields,
    'operationalStatus'
  )

  unitRaw.data.name = name?.value ? (name?.value as string) : undefined
  unitRaw.data.erp_id = erpID?.value ? (erpID?.value as string) : undefined
  unitRaw.data.area = area?.value ? (area?.value as number) : undefined
  unitRaw.data.description = getStringFieldByDataKey(
    convertedFormFields,
    'description'
  )
  unitRaw.data.operationalStatus = operationalStatus?.value
    ? (operationalStatus.value as string)
    : unitRaw.data.operationalStatus
  unitRaw.data.area_description = areaDescription?.value
    ? (areaDescription?.value as string)
    : undefined
  unitRaw.data.custom_fields = customFields.map((customField) => {
    const inputValue =
      getFormFieldByDataKey(convertedFormFields, customField.key)?.value ||
      customField.defaultValue

    return {
      fieldName: customField.fieldName,
      inputValue: inputValue,
    }
  })
  return unitRaw
}

export function sortTableByField(
  formFieldsByUniqueID: EditableFeatureType[],
  sortBy: SortTableByField[]
): EditableFeatureType[] {
  formFieldsByUniqueID.sort((formFieldsA, formFieldsB) => {
    const aFields = formFieldsA
    const bFields = formFieldsB

    let result = 0
    sortBy.some((sortField) => {
      const { fieldName, order } = sortField
      const aValue = getFormFieldByDataKey(aFields.formFields, fieldName)
        ?.value as number
      const bValue = getFormFieldByDataKey(bFields.formFields, fieldName)
        ?.value as number

      if (aValue < bValue) {
        result = order === 'asc' ? -1 : 1
        return true
      } else if (aValue > bValue) {
        result = order === 'asc' ? 1 : -1
        return true
      }
      return false
    })

    return result
  })

  return formFieldsByUniqueID
}
